import React, { useState, useEffect } from "react";
import { Box, ExpandableSection, Grid, SegmentedControl, SpaceBetween, StatusIndicator } from "@amzn/awsui-components-react";
import SideNavigation from "@amzn/awsui-components-console/side-navigation";
import aiInsights from '../../../images/aiInsights.svg'
import '../styles.scss';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { convertToLocalTime } from "src/Ateam-BI/CorpReporting/Utility";
import { getJSONSideNavInfo, getResultContent, loadResultsFromS3, VIEW_OPTION_IDS, VIEW_OPTIONS } from "../Utility";
import ReactMarkdownRenderer from "./ReactMarkdownRenderer";
import DownloadResultsButton from "./DownloadResultsButton";
import ShareLinkButton from "./ShareLinkButton";

const JSONResultsTopBar = ({viewOption, setViewOption, data}) => {
  return (
    <Box>
      <Box float="left"></Box>
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <ShareLinkButton data={data} />
          <DownloadResultsButton data={data} />
          <div className="view-option-controls">
            <SegmentedControl
              selectedId={viewOption}
              onChange={({ detail }) =>
                setViewOption(detail.selectedId)
              }
              label="Results View"
              options={VIEW_OPTIONS}
            />
          </div>
        </SpaceBetween>
      </Box>
    </Box>
  )
}

const JSONResultsSideNav = ({sideNavInfo, activeHref, setActiveHref}) => {
  return (
    <div className="side-nav-sticky">
      <SideNavigation
        activeHref={activeHref}
        header={{ href: "#/status-report", text: "Overall summary" }}
        onFollow={event => {
          if (!event.detail.external) {
            event.preventDefault();
            const href = event.detail.href;
            setActiveHref(href);
            const targetElement = document.getElementById(href);
            if (targetElement) {
              const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top: elementPosition - 118, behavior: 'smooth' });
            }
          }
        }}
        items={sideNavInfo}
      />
    </div>
  );
}


const HTMLResultsView = ({ viewResultsRequestDetails, viewOption, setViewOption, activeHref, setActiveHref, data }) => {
  const showSideNav = viewOption === VIEW_OPTION_IDS.ALL;
  return (
    <Grid gridDefinition={showSideNav ? [{ colspan: 2 }, { colspan: 10 }] : [{ colspan: 12 }]}>
      {showSideNav && <JSONResultsSideNav sideNavInfo={viewResultsRequestDetails.sideNavInfo} activeHref={activeHref} setActiveHref={setActiveHref}/>}
      <SpaceBetween direction="vertical" size="m">
        <JSONResultsTopBar viewOption={viewOption} setViewOption={setViewOption} data={data}/>
        {
          viewResultsRequestDetails?.resultObject?.length ?
            viewResultsRequestDetails.resultObject.map((item, i) => {
              if (item.group) {
                return <React.Fragment key={i}>
                  <div id={`#/${item.sectionId}`} className="scroll-target"></div>
                    <ExpandableSection
                      defaultExpanded
                      headerText={item.header}
                    >
                      <SpaceBetween direction="vertical" size="l">
                        {
                          item.childContent?.map((child, j) => {
                            return <React.Fragment key={j}>
                              <div id={`#/${child.checkId}`} className="scroll-target"></div>
                              <div className="firstpass-expandable-section">
                                <ExpandableSection
                                  defaultExpanded
                                  variant="container"
                                  headerText={child.header}
                                >
                                  <Box>{child.content}</Box>
                                </ExpandableSection>
                              </div>
                            </React.Fragment>
                          })
                        }
                      </SpaceBetween>
                    </ExpandableSection>
                </React.Fragment>
              } else {
                return (
                  <React.Fragment key={i}>
                    <div id={`#/${item.sectionId || item.checkId}`} className="scroll-target"></div>
                    <div className="firstpass-expandable-section">
                      <ExpandableSection
                        defaultExpanded
                        variant="container"
                        headerText={item.header}
                      >
                        <Box>{item.content}</Box>
                      </ExpandableSection>
                    </div>
                  </React.Fragment>
                )
              }
            })
            :
            <Box color="text-secondary" variant="p">
              No Data Found
            </Box>
        }
      </SpaceBetween>
    </Grid>
  );
};

const MarkdownResultsView = ({ viewResultsRequestDetails }) => {
  return (
    <Grid gridDefinition={[{ colspan: 12 }]}>
      <SpaceBetween direction="vertical" size="m">
        {
          viewResultsRequestDetails?.resultObject?.length ?
            viewResultsRequestDetails.resultObject.map((item, i) => {
              if (item.group) {
                return <React.Fragment key={i}>
                  <ExpandableSection
                    defaultExpanded
                    headerText={item.header}
                  >
                    <SpaceBetween direction="vertical" size="l">
                      {
                        item.childContent?.map((child, j) => {
                          return <React.Fragment key={j}>
                            <ExpandableSection
                              defaultExpanded
                              variant="container"
                              headerText={child.header}
                            >
                              <ReactMarkdownRenderer content={child.content} />
                            </ExpandableSection>
                          </React.Fragment>
                        })
                      }
                    </SpaceBetween>
                  </ExpandableSection>
                </React.Fragment>
              } else {
                return (
                  <React.Fragment key={i}>
                    <ExpandableSection
                      defaultExpanded
                      variant="container"
                      headerText={item.header}
                    >
                      <ReactMarkdownRenderer content={item.content} />
                    </ExpandableSection>
                  </React.Fragment>
                )
              }
            })
            :
            <Box color="text-secondary" variant="p">
              No Data Found
            </Box>
        }
      </SpaceBetween>
    </Grid>
  );
};

const FirstPassResultComponent = (props) => {
  const [viewResultsRequestDetails, setViewResultsRequestDetails] = useState(null)
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [viewOption, setViewOption] = useState(VIEW_OPTION_IDS.EXCEPTION);
  const [activeHref, setActiveHref] = useState(`#/`);


  useEffect(() => {
    if (props?.data) {
      loadView()
    }

  }, [props]);

  const loadView = () => {
    fetchDetails(props.data)
  }

  const fetchDetails = async (data) => {
    setIsResultsLoading(true);
    const s3Data = await loadResultsFromS3(data);
    setResultsData(s3Data);
    setIsResultsLoading(false)
  }

  useEffect(() => {
    if (resultsData) {
      const newViewResultsRequestDetail = {
        resultObject: getResultContent({...resultsData}, viewOption),
        sideNavInfo: getJSONSideNavInfo({...resultsData}),
        ...props.data
      }
      setViewResultsRequestDetails(newViewResultsRequestDetail)
    }
  }, [resultsData, viewOption])

  const isHtmlStyleResults = (viewResultsRequestDetails?.resultObject?.[0]?.contentType) === "html";

  return (
    <SpaceBetween direction="vertical" size="m">
      {
        isResultsLoading && <StatusIndicator type="loading">
          Loading
        </StatusIndicator>
      }

      {
        !isResultsLoading && viewResultsRequestDetails && <SpaceBetween direction="vertical" size="l">

          <SpaceBetween direction="horizontal" size="m">
            <img
              className="ai-button"
              src={aiInsights}
              alt=""
            />
            <Box variant="awsui-key-label"> You are viewing results for Entity Name: {viewResultsRequestDetails?.entity_name || '-'}, Execution Date: {convertToLocalTime(viewResultsRequestDetails?.created_at, true) || '-'}, Current year: {viewResultsRequestDetails?.current_year || '-'}, Prior Year: {viewResultsRequestDetails?.previous_year || '-'} </Box>
          </SpaceBetween>

          {isHtmlStyleResults ? (
            <HTMLResultsView
              viewResultsRequestDetails={viewResultsRequestDetails}
              viewOption={viewOption}
              setViewOption={setViewOption}
              activeHref={activeHref}
              setActiveHref={setActiveHref}
              data={props.data}
            />
          ) : (
            <MarkdownResultsView
              viewResultsRequestDetails={viewResultsRequestDetails}
            />
          )}
        </SpaceBetween>
      }
    </SpaceBetween>

  );
};

export default FirstPassResultComponent;
